import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppLanguage } from "../i18n";
import { RootState } from "../store";
import {
  getAvailableLanguages,
  normalizeLanguage,
  resolveLanguage,
} from "../i18n/language";

type LanguageCode = AppLanguage;

const PRIMARY_COLOR = "#280F6D";

const languages: { code: LanguageCode; label: string; title: string }[] = [
  { code: "ru", label: "RU", title: "Русский" },
  { code: "en", label: "EN", title: "English" },
  { code: "ar", label: "AR", title: "العربية" },
  { code: "hy", label: "HY", title: "Հայերեն" },
  { code: "tr", label: "TR", title: "Türkçe" },
];

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const provider = useSelector((state: RootState) => state.app.provider);
  const roles = useSelector((state: RootState) => state.user.roles);

  const language =
    normalizeLanguage(i18n.resolvedLanguage || i18n.language) ?? "ru";
  const availableLanguages: LanguageCode[] = getAvailableLanguages(
    provider,
    roles,
  );
  const visibleLanguages = useMemo(
    () => languages.filter((item) => availableLanguages.includes(item.code)),
    [availableLanguages],
  );

  useEffect(() => {
    if (availableLanguages.includes(language)) {
      return;
    }

    const fallbackLanguage = resolveLanguage({
      provider,
      roles,
      fallbackLanguage: availableLanguages[0],
    });

    void i18n.changeLanguage(fallbackLanguage);
    localStorage.setItem("language", fallbackLanguage);
  }, [availableLanguages, i18n, language, provider, roles]);

  const handleChange = async (event: SelectChangeEvent) => {
    const nextLanguage = event.target.value as LanguageCode;

    await i18n.changeLanguage(nextLanguage);
    localStorage.setItem("language", nextLanguage);
  };

  return (
    <Wrapper>
      <FormControl size="small">
        <Select
          value={language}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            const current =
              visibleLanguages.find((item) => item.code === selected) ??
              languages.find((item) => item.code === selected);

            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <LanguageIcon sx={{ fontSize: 20, color: PRIMARY_COLOR }} />
                <span>{current?.label}</span>
              </Box>
            );
          }}
          sx={{
            width: "92px",
            height: "36px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",

            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: PRIMARY_COLOR,
            },

            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: PRIMARY_COLOR,
            },

            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: PRIMARY_COLOR,
              borderWidth: "1px",
            },

            "& .MuiSelect-select": {
              padding: "7px 28px 7px 10px",
            },
          }}
        >
          {visibleLanguages.map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.label} — {item.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Wrapper>
  );
};

export default LanguageSwitcher;
